import {
  avatar,
  background,
  captions,
  host,
  speaker,
  text,
  visualizer,
} from './helpers'
import type { TemplateBlueprint } from './types'

/**
 * `ScriptSmallLayout` (`layouts/ScriptSmallLayout.tsx`): the speaker's
 * avatar and handle on top — beside the bars in landscape — captions, bars,
 * and the episode (host picture, title, subtitle) at the foot. One line each.
 * Intended differences: v1 outlined the speaker in a pill and the episode in
 * a rounded card; v2 draws neither — the card ran under the watermark's
 * corner, and the outlines fought the bars for room. In landscape the bars
 * start where the pill ended plus 50 px and stop 50 px from the right edge.
 * Intended difference (#547): the subtitle ends at x 810 in portrait and
 * square, so a long one is cut clear of the watermark.
 */
const bars = { variant: 'bold' as const }
const handle = { weight: 400, lineHeight: 1.26 }
const episode = { weight: 500, lineHeight: 1.62 }

export const scriptSmallBlueprint: TemplateBlueprint = {
  kind: 'audio',
  orientations: {
    portrait: [
      background(),
      avatar('portrait', 72, 72, 72),
      {
        ...speaker('portrait', [164, 87, 600, 43], 'handle', {
          size: 34,
          ...handle,
        }),
        id: 'speaker',
      },
      captions('portrait', [100, 272, 880, 937], 'start'),
      visualizer('portrait', [100, 1324, 880, 220], bars),
      host('portrait', 82, 1688, 150, 'rounded'),
      text('portrait', 'title', [262, 1705, 760, 55], { size: 34, ...episode }),
      text('portrait', 'subtitle', [262, 1760, 548, 52], {
        size: 32,
        ...episode,
      }),
    ],
    square: [
      background(),
      avatar('square', 68, 68, 62),
      {
        ...speaker('square', [146, 79, 600, 40], 'handle', {
          size: 32,
          ...handle,
        }),
        id: 'speaker',
      },
      captions('square', [50, 215, 980, 249], 'start'),
      visualizer('square', [50, 540, 980, 220], bars),
      host('square', 82, 863, 135, 'rounded', { radius: 30 }),
      text('square', 'title', [247, 878, 790, 55], { size: 34, ...episode }),
      text('square', 'subtitle', [247, 933, 563, 52], { size: 32, ...episode }),
    ],
    landscape: [
      background(),
      avatar('landscape', 72, 94, 72),
      {
        ...speaker('landscape', [164, 109, 380, 43], 'handle', {
          size: 34,
          ...handle,
        }),
        id: 'speaker',
      },
      visualizer('landscape', [616, 50, 1254, 160], bars),
      captions('landscape', [100, 277, 1720, 474], 'start'),
      host('landscape', 82, 848, 150, 'rounded'),
      text('landscape', 'title', [272, 865, 1500, 55], {
        size: 34,
        ...episode,
      }),
      text('landscape', 'subtitle', [272, 920, 1500, 52], {
        size: 32,
        ...episode,
      }),
    ],
  },
}
